/* Which optional notification emails the company receives (owner / HR). Security and billing emails are always sent. */
import { useNotificationPrefs, useUpdateNotificationPrefs } from "../../data/hooks";
import type { NotificationPref } from "../../data/types";
import { useI18n } from "../../i18n";
import { Alert, Card, ErrorBox, Skeleton, Toggle, errMsg, useToast } from "../../ui";

export const PREF_LABELS: Record<string, [string, string, string, string]> = {
  trial_reminders: ["Trial reminders", "تذكيرات الفترة التجريبية", "7, 3 and 1 day before the trial ends, and when it ends", "قبل انتهاء التجربة بـ 7 و3 أيام ويوم واحد، وعند انتهائها"],
  leave_submitted: ["New leave requests", "طلبات الإجازة الجديدة", "Owners and HR are told when someone requests leave", "إشعار المالك والموارد البشرية عند طلب إجازة"],
  leave_decided: ["Leave decisions", "قرارات الإجازة", "Employees hear when their leave is approved or declined", "إشعار الموظف بالموافقة على إجازته أو رفضها"],
  request_decided: ["Loan & expense decisions", "قرارات السلف والمصروفات", "Employees hear when a loan or expense claim is decided", "إشعار الموظف بقرار السلفة أو المطالبة"],
  letter_issued: ["Letters issued", "إصدار الخطابات", "Employees get a message when HR issues their letter", "إشعار الموظف عند إصدار خطابه"],
  payroll_approved: ["Payroll approved", "اعتماد الرواتب", "Owner and accountant hear when the run is approved", "إشعار المالك والمحاسب عند اعتماد المسير"],
  payslips_available: ["Payslips available", "قسائم الراتب", "Each employee is told when salary is paid", "إشعار كل موظف عند صرف الراتب"],
  resignation: ["Resignations", "الاستقالات", "New resignations and acknowledgements", "الاستقالات الجديدة وتأكيد استلامها"],
  settlement_ready: ["Final settlements", "المخالصات النهائية", "When clearance completes and the settlement is ready", "عند اكتمال إخلاء الطرف وجاهزية المخالصة"],
  documents_digest: ["Document expiry digest", "ملخص انتهاء الوثائق", "Daily list of documents overdue or expiring in 30/60/90 days", "قائمة يومية بالوثائق المنتهية أو التي تنتهي خلال 30/60/90 يومًا"],
  device_offline: ["Biometric device offline", "انقطاع جهاز البصمة", "HR is alerted when a device stops reporting", "تنبيه الموارد البشرية عند انقطاع جهاز"],
  unmatched_digest: ["Unmatched device users", "مستخدمو الأجهزة غير المطابقين", "Weekly list of punches not linked to an employee", "قائمة أسبوعية بالبصمات غير المرتبطة بموظف"],
};
const GROUPS: [NotificationPref["group"], string, string][] = [["billing", "Billing", "الفوترة"], ["hr", "HR workflow", "سير عمل الموارد البشرية"], ["compliance", "Compliance & devices", "الامتثال والأجهزة"]];

export default function NotificationPrefsCard({ readOnly }: { readOnly?: boolean }) {
  const { L } = useI18n();
  const toast = useToast();
  const q = useNotificationPrefs({ enabled: !readOnly });
  const m = useUpdateNotificationPrefs();
  if (readOnly) return null;
  const set = async (key: string, on: boolean) => {
    try {
      await m.mutateAsync({ [key]: on });
      toast(on ? L("Notification turned on", "تم تفعيل الإشعار") : L("Notification turned off", "تم إيقاف الإشعار"));
    } catch (e) { toast(errMsg(e), "bad"); }
  };
  return (
    <Card title={L("Email notifications", "إشعارات البريد")} sub={L("Choose which optional emails your company receives.", "اختر رسائل البريد الاختيارية التي تصل إلى منشأتك.")} testId="notif-prefs">
      {q.error ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : !q.data ? <Skeleton /> : (
        <div className="stack">
          <Alert tone="info" tint title={L("Security and billing emails are always sent", "رسائل الأمان والفوترة تُرسل دائمًا")} desc={L("Sign-in, password, verification, receipts and invoices cannot be turned off.", "لا يمكن إيقاف رسائل الدخول وكلمة المرور والتحقق والإيصالات والفواتير.")} />
          {GROUPS.map(([g, en, ar]) => {
            const items = q.data.filter((p) => p.group === g);
            if (!items.length) return null;
            return (
              <div key={g} className="stack" style={{ gap: 6 }}>
                <b className="sm">{L(en, ar)}</b>
                {items.map((p) => {
                  const lab = PREF_LABELS[p.key] ?? [p.key, p.key, "", ""];
                  return (
                    <div key={p.key} className="list-row" data-testid={"pref-" + p.key}>
                      <span className="grow"><b className="sm">{L(lab[0], lab[1])}</b><span className="xs muted" style={{ display: "block" }}>{L(lab[2], lab[3])}</span></span>
                      <Toggle checked={p.enabled} onChange={(v) => set(p.key, v)} label={L(lab[0], lab[1])} testId={"pref-toggle-" + p.key} disabled={m.isPending} />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
